import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseDate, formatDateBR } from "../utils/dateUtils";
import {
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  PiggyBank,
  Gift,
  Calendar,
  CreditCard,
  Banknote,
  QrCode,
  AlertCircle,
  CheckCircle,
  Clock,
  Repeat
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoriaOptions = {
  entrada: ["dizimo", "oferta", "doacao", "outro"],
  saida: ["aluguel", "energia", "agua", "internet", "manutencao", "compras", "salario", "outro"]
};

const recorrenciaOptions = [
  { value: "unica", label: "Única (não repete)" },
  { value: "semanal", label: "Semanal" },
  { value: "mensal", label: "Mensal" },
  { value: "anual", label: "Anual" }
];

const formaPagamentoOptions = [
  { value: "dinheiro", label: "Dinheiro", icon: Banknote },
  { value: "pix", label: "PIX", icon: QrCode },
  { value: "cartao", label: "Cartão", icon: CreditCard }
];

const initialFormState = {
  tipo: "entrada",
  categoria: "",
  descricao: "",
  valor: "",
  data: new Date().toISOString().split("T")[0],
  vencimento: "",
  pago: true,
  forma_pagamento: "dinheiro",
  recorrencia: "unica",
  quantidade_recorrencia: 1,
  observacoes: ""
};

export default function FinancePage() {
  const { user, currentBranch } = useAuth();
  const [finances, setFinances] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFinance, setEditingFinance] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [activeTab, setActiveTab] = useState("resumo");

  const canEdit = () => {
    if (user?.perfil_acesso_id) {
      const permissoes = user?.permissoes || {};
      return permissoes?.financeiro?.criar || permissoes?.financeiro?.editar;
    }
    return true;
  };

  useEffect(() => {
    loadData();
  }, [currentBranch]);

  const loadData = async () => {
    try {
      const filialParam = currentBranch ? `?filial_id=${currentBranch.id}` : '';
      const [financesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/finance${filialParam}`),
        axios.get(`${API}/finance/summary${filialParam}`)
      ]);
      setFinances(financesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados financeiros");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...form,
        valor: parseFloat(form.valor),
        quantidade_recorrencia: parseInt(form.quantidade_recorrencia) || 1,
        filial_id: currentBranch?.id || null
      };
      
      if (editingFinance) {
        await axios.put(`${API}/finance/${editingFinance.id}`, data);
        toast.success("Registro atualizado!");
      } else {
        await axios.post(`${API}/finance`, data);
        toast.success("Registro adicionado!");
      }
      setDialogOpen(false);
      setEditingFinance(null);
      setForm(initialFormState);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (finance) => {
    setEditingFinance(finance);
    setForm({
      ...finance,
      valor: finance.valor.toString(),
      vencimento: finance.vencimento || "",
      pago: finance.pago !== false,
      forma_pagamento: finance.forma_pagamento || "dinheiro",
      recorrencia: finance.recorrencia || "unica",
      quantidade_recorrencia: finance.quantidade_recorrencia || 1,
      observacoes: finance.observacoes || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover este registro?")) return;
    try {
      await axios.delete(`${API}/finance/${id}`);
      toast.success("Registro removido");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const handleTogglePago = async (finance) => {
    try {
      await axios.put(`${API}/finance/${finance.id}`, {
        ...finance,
        pago: !finance.pago
      });
      toast.success(finance.pago ? "Marcado como não pago" : "Marcado como pago");
      loadData();
    } catch (error) {
      toast.error("Erro ao atualizar");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value || 0);
  };

  const getCategoriaLabel = (categoria) => {
    const labels = {
      dizimo: "Dízimo",
      oferta: "Oferta",
      doacao: "Doação",
      aluguel: "Aluguel",
      energia: "Energia",
      agua: "Água",
      internet: "Internet",
      manutencao: "Manutenção",
      compras: "Compras",
      salario: "Salário",
      outro: "Outro"
    };
    return labels[categoria] || categoria;
  };

  const getFormaPagamentoLabel = (forma) => {
    const opt = formaPagamentoOptions.find(f => f.value === forma);
    return opt?.label || forma;
  };

  const entradas = finances.filter(f => f.tipo === "entrada");
  const saidas = finances.filter(f => f.tipo === "saida");
  const vencimentosAbertos = finances.filter(f => !f.pago && f.vencimento);

  // Ordenar vencimentos por data
  const vencimentosOrdenados = [...vencimentosAbertos].sort((a, b) => 
    parseDate(a.vencimento) - parseDate(b.vencimento)
  );

  const isVencido = (vencimento) => {
    if (!vencimento) return false;
    return parseDate(vencimento) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderFinanceCard = (finance, isEntrada) => (
    <Card key={finance.id} className="card-hover" data-testid={`finance-card-${finance.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded-xl ${isEntrada ? "bg-green-500/10" : "bg-red-500/10"}`}>
              {isEntrada ? (
                <TrendingUp className="h-5 w-5 text-green-500" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium">{finance.descricao}</p>
                {!finance.pago && (
                  <Badge variant="outline" className="text-orange-500 border-orange-500">
                    <Clock className="h-3 w-3 mr-1" />
                    Pendente
                  </Badge>
                )}
                {finance.pago && (
                  <Badge className="bg-green-500/10 text-green-500">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Pago
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="secondary" className="text-xs">
                  {getCategoriaLabel(finance.categoria)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDateBR(finance.data)}
                </span>
                {finance.vencimento && (
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${isVencido(finance.vencimento) && !finance.pago ? "text-red-500 border-red-500" : ""}`}
                  >
                    Venc: {formatDateBR(finance.vencimento)}
                  </Badge>
                )}
                {finance.forma_pagamento && (
                  <Badge variant="outline" className="text-xs">
                    {getFormaPagamentoLabel(finance.forma_pagamento)}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`text-lg font-bold ${isEntrada ? "text-green-500" : "text-red-500"}`}>
              {isEntrada ? "" : "-"}{formatCurrency(finance.valor)}
            </span>
            {canEdit() && (
              <div className="flex gap-1">
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8"
                  onClick={() => handleTogglePago(finance)}
                  title={finance.pago ? "Marcar como não pago" : "Marcar como pago"}
                >
                  {finance.pago ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-orange-500" />
                  )}
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleEdit(finance)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDelete(finance.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="financeiro-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Financeiro</h1>
        {canEdit() && (
          <Button onClick={() => { setEditingFinance(null); setForm(initialFormState); setDialogOpen(true); }} data-testid="add-finance-button">
            <Plus className="h-4 w-4 mr-2" />
            Novo Registro
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="resumo" data-testid="tab-resumo" className="relative">
            Resumo
            {vencimentosAbertos.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {vencimentosAbertos.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="movimento" data-testid="tab-movimento">Movimento</TabsTrigger>
        </TabsList>

        <TabsContent value="resumo" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover" data-testid="card-saldo">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${summary.saldo >= 0 ? "bg-green-500/10" : "bg-red-500/10"}`}>
                    <Wallet className={`h-5 w-5 ${summary.saldo >= 0 ? "text-green-500" : "text-red-500"}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Saldo</p>
                    <p className={`text-lg font-bold ${summary.saldo >= 0 ? "text-green-500" : "text-red-500"}`}>
                      {formatCurrency(summary.saldo)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="card-entradas">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-green-500/10">
                    <ArrowUpCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Entradas</p>
                    <p className="text-lg font-bold text-green-500">{formatCurrency(summary.total_entradas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="card-saidas">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-red-500/10">
                    <ArrowDownCircle className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Saídas</p>
                    <p className="text-lg font-bold text-red-500">{formatCurrency(summary.total_saidas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="card-pendentes">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-orange-500/10">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Pendentes</p>
                    <p className="text-lg font-bold text-orange-500">{vencimentosAbertos.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Detalhes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <PiggyBank className="h-4 w-4 text-primary" />
                  <span>Total em Dízimos</span>
                </div>
                <span className="font-semibold">{formatCurrency(summary.total_dizimos)}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-orange-500" />
                  <span>Total em Ofertas</span>
                </div>
                <span className="font-semibold">{formatCurrency(summary.total_ofertas)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Vencimentos em Aberto - Movido para Resumo */}
          {vencimentosOrdenados.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  Vencimentos em Aberto ({vencimentosAbertos.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {vencimentosOrdenados.map((finance) => (
                  <div 
                    key={finance.id} 
                    className={`p-3 rounded-lg border ${isVencido(finance.vencimento) ? "border-red-500/50 bg-red-500/5" : "border-orange-500/50 bg-orange-500/5"}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-xl ${isVencido(finance.vencimento) ? "bg-red-500/10" : "bg-orange-500/10"}`}>
                          {isVencido(finance.vencimento) ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-orange-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{finance.descricao}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {getCategoriaLabel(finance.categoria)}
                            </Badge>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${isVencido(finance.vencimento) ? "text-red-500 border-red-500" : "text-orange-500 border-orange-500"}`}
                            >
                              {isVencido(finance.vencimento) ? "Vencido: " : "Vence: "}
                              {formatDateBR(finance.vencimento)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`text-sm font-bold ${finance.tipo === "entrada" ? "text-green-500" : "text-red-500"}`}>
                          {formatCurrency(finance.valor)}
                        </span>
                        <Button 
                          size="sm" 
                          onClick={() => handleTogglePago(finance)}
                          className="bg-green-500 hover:bg-green-600 h-7 text-xs"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Baixar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="movimento" className="space-y-4 mt-4">
          {finances.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Nenhum lançamento registrado</p>
              </CardContent>
            </Card>
          ) : (
            finances
              .sort((a, b) => parseDate(b.data) - parseDate(a.data))
              .map((finance) => renderFinanceCard(finance, finance.tipo === "entrada"))
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFinance ? "Editar Registro" : "Novo Registro"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select
                  value={form.tipo}
                  onValueChange={(v) => setForm({ ...form, tipo: v, categoria: "" })}
                >
                  <SelectTrigger data-testid="finance-tipo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrada">
                      <div className="flex items-center gap-2">
                        <ArrowUpCircle className="h-4 w-4 text-green-500" />
                        Entrada
                      </div>
                    </SelectItem>
                    <SelectItem value="saida">
                      <div className="flex items-center gap-2">
                        <ArrowDownCircle className="h-4 w-4 text-red-500" />
                        Saída
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select
                  value={form.categoria}
                  onValueChange={(v) => setForm({ ...form, categoria: v })}
                >
                  <SelectTrigger data-testid="finance-categoria">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriaOptions[form.tipo].map((cat) => (
                      <SelectItem key={cat} value={cat}>{getCategoriaLabel(cat)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input
                required
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Descrição do lançamento"
                data-testid="finance-descricao"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={form.valor}
                  onChange={(e) => setForm({ ...form, valor: e.target.value })}
                  placeholder="0,00"
                  data-testid="finance-valor"
                />
              </div>

              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  required
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  data-testid="finance-data"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vencimento</Label>
                <Input
                  type="date"
                  value={form.vencimento}
                  onChange={(e) => setForm({ ...form, vencimento: e.target.value })}
                  data-testid="finance-vencimento"
                />
              </div>

              <div className="space-y-2">
                <Label>Forma de Pagamento</Label>
                <Select
                  value={form.forma_pagamento}
                  onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}
                >
                  <SelectTrigger data-testid="finance-forma-pagamento">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {formaPagamentoOptions.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Recorrência</Label>
                <Select
                  value={form.recorrencia}
                  onValueChange={(v) => setForm({ ...form, recorrencia: v })}
                >
                  <SelectTrigger data-testid="finance-recorrencia">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {recorrenciaOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.recorrencia !== "unica" && (
                <div className="space-y-2">
                  <Label>Quantidade de vezes</Label>
                  <Input
                    type="number"
                    min="1"
                    max="60"
                    value={form.quantidade_recorrencia}
                    onChange={(e) => setForm({ ...form, quantidade_recorrencia: e.target.value })}
                    data-testid="finance-quantidade-recorrencia"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <CheckCircle className={`h-4 w-4 ${form.pago ? "text-green-500" : "text-muted-foreground"}`} />
                <span className="text-sm font-medium">Pago / Baixado</span>
              </div>
              <Switch
                checked={form.pago}
                onCheckedChange={(checked) => setForm({ ...form, pago: checked })}
                data-testid="finance-pago"
              />
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                placeholder="Observações adicionais..."
                rows={2}
                data-testid="finance-observacoes"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-finance-button">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
